import { createFileRoute } from "@tanstack/react-router";
import { MerchantExperienceBar } from "@/components/renata/MerchantExperienceBar";
import { useEffect, useMemo, useState } from "react";
import { ClinicalPageShell, ClinicalStatCard } from "@/components/clinical";
import { CrudShellHeader, Loading } from "@/components/ds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Eye, EyeOff, MessageSquare, Star, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { apiDelete, apiGetJson, apiSendJson } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import type { ServiceReview } from "@/lib/db-service/service-reviews";

export const Route = createFileRoute("/app/resenas")({
  component: Page,
  head: () => ({ meta: [{ title: "Reseñas" }] }),
});

function Stars({ rating, size = 16 }: { rating: number; size?: number }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          style={{ width: size, height: size }}
          className={cn(i <= rating ? "fill-amber-400 text-amber-400" : "text-muted-foreground/30")}
        />
      ))}
    </div>
  );
}

function Page() {
  const tenantId = useWorkspaceTenantId();
  const [reviews, setReviews] = useState<ServiceReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterRating, setFilterRating] = useState<number | null>(null);
  const [filterVisibility, setFilterVisibility] = useState<"all" | "public" | "hidden">("all");
  const [moderating, setModerating] = useState<string | null>(null);

  async function load() {
    if (!tenantId) return;
    setLoading(true);
    try {
      const r = await apiGetJson<{ items: ServiceReview[] }>("/api/service-admin?resource=reviews", tenantId);
      setReviews(r.items ?? []);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudo cargar");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, [tenantId]);

  async function handleDelete(id: string) {
    if (!tenantId) return;
    try {
      await apiDelete(`/api/service-admin?resource=reviews&id=${id}`, tenantId);
      setReviews((prev) => prev.filter((r) => r.id !== id));
      toast.success("Reseña eliminada");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudo eliminar la reseña");
    }
  }

  // SCRUM-424: moderar sin borrar — ocultar del storefront (isPublic=false)
  // o volver a publicar, conservando el historial.
  async function toggleVisibility(r: ServiceReview) {
    if (!tenantId || moderating) return;
    setModerating(r.id);
    try {
      await apiSendJson(`/api/service-admin?resource=reviews&id=${r.id}`, "PATCH", { isPublic: !r.isPublic }, tenantId);
      setReviews((prev) => prev.map((x) => (x.id === r.id ? { ...x, isPublic: !r.isPublic } : x)));
      toast.success(r.isPublic ? "Reseña ocultada" : "Reseña publicada");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudo actualizar");
    } finally {
      setModerating(null);
    }
  }

  const filtered = useMemo(() => {
    let out = reviews;
    if (filterRating !== null) out = out.filter((r) => r.rating === filterRating);
    if (filterVisibility === "public") out = out.filter((r) => r.isPublic);
    if (filterVisibility === "hidden") out = out.filter((r) => !r.isPublic);
    if (search.trim()) {
      const q = search.toLowerCase();
      out = out.filter(
        (r) =>
          r.customerEmail.toLowerCase().includes(q) ||
          (r.professionalName ?? "").toLowerCase().includes(q) ||
          (r.serviceName ?? "").toLowerCase().includes(q) ||
          (r.comment ?? "").toLowerCase().includes(q),
      );
    }
    return [...out].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }, [reviews, search, filterRating]);

  const avgRating = reviews.length ? reviews.reduce((s, r) => s + r.rating, 0) / reviews.length : 0;

  const dist = [5, 4, 3, 2, 1].map((star) => {
    const count = reviews.filter((r) => r.rating === star).length;
    return { star, count, pct: reviews.length ? (count / reviews.length) * 100 : 0 };
  });

  return (
    <ClinicalPageShell offset="none" className="pt-6">
      <MerchantExperienceBar screenId="resenas" />
      <CrudShellHeader title="Reseñas" subtitle="Feedback de clientes post-turno" count={reviews.length} />
      {loading ? (
        <Loading />
      ) : (
        <div className="space-y-4 px-4 pb-8">
          <div className="grid gap-3 sm:grid-cols-3">
            <ClinicalStatCard label="Total reseñas" value={reviews.length} icon={MessageSquare} variant="primary" />
            <ClinicalStatCard
              label="Promedio"
              value={avgRating > 0 ? avgRating.toFixed(1) : "—"}
              hint={avgRating > 0 ? "de 5 estrellas" : "Sin reseñas aún"}
              icon={Star}
              variant="success"
            />
            <div className="rounded-xl border bg-card p-5">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Distribución</p>
              <div className="mt-3 space-y-1.5">
                {dist.map(({ star, count, pct }) => (
                  <div key={star} className="flex items-center gap-2 text-xs">
                    <span className="w-3 text-right tabular-nums text-muted-foreground">{star}</span>
                    <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                    <div className="flex-1 overflow-hidden rounded-full bg-secondary/50">
                      <div className="h-1.5 rounded-full bg-amber-400 transition-all" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="w-5 text-right tabular-nums text-muted-foreground">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <Input placeholder="Buscar por email, profesional o servicio…" value={search} onChange={(e) => setSearch(e.target.value)} />

          <div className="flex flex-wrap gap-1.5">
            <Button size="sm" variant={filterRating === null ? "default" : "outline"} onClick={() => setFilterRating(null)}>
              Todas
            </Button>
            {[5, 4, 3, 2, 1].map((s) => (
              <Button
                key={s}
                size="sm"
                variant={filterRating === s ? "default" : "outline"}
                className="gap-1"
                onClick={() => setFilterRating(filterRating === s ? null : s)}
              >
                {s} <Star className="h-3 w-3" />
              </Button>
            ))}
          </div>

          <div className="flex flex-wrap gap-1.5" data-testid="reviews-visibility-filter">
            <Button size="sm" variant={filterVisibility === "all" ? "default" : "outline"} onClick={() => setFilterVisibility("all")}>
              Todos los estados
            </Button>
            <Button size="sm" variant={filterVisibility === "public" ? "default" : "outline"} className="gap-1" onClick={() => setFilterVisibility("public")}>
              <Eye className="h-3 w-3" /> Públicas
            </Button>
            <Button size="sm" variant={filterVisibility === "hidden" ? "default" : "outline"} className="gap-1" onClick={() => setFilterVisibility("hidden")}>
              <EyeOff className="h-3 w-3" /> Ocultas
            </Button>
          </div>

          {filtered.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-16 text-center">
              <Star className="h-10 w-10 text-muted-foreground/30" />
              <p className="text-sm text-muted-foreground">
                {reviews.length === 0
                  ? "Aún no hay reseñas. Llegarán automáticamente después de cada turno completado."
                  : "Sin resultados para el filtro aplicado."}
              </p>
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {filtered.map((r) => (
                <div key={r.id} className="space-y-3 rounded-xl border bg-card p-4">
                  <div className="flex items-start justify-between gap-2">
                    <Stars rating={r.rating} />
                    <div className="flex items-center gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-muted-foreground hover:text-foreground"
                        disabled={moderating === r.id}
                        title={r.isPublic ? "Ocultar del storefront" : "Publicar en el storefront"}
                        onClick={() => void toggleVisibility(r)}
                      >
                        {r.isPublic ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-destructive">
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>¿Eliminar reseña?</AlertDialogTitle>
                            <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction onClick={() => void handleDelete(r.id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                              Eliminar
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>

                  {r.comment ? (
                    <p className="text-sm leading-relaxed text-foreground/80">&ldquo;{r.comment}&rdquo;</p>
                  ) : (
                    <p className="text-xs italic text-muted-foreground/60">Sin comentario</p>
                  )}

                  <div className="space-y-0.5 text-xs text-muted-foreground">
                    <p>{r.customerEmail}</p>
                    {r.serviceName && (
                      <p>
                        {r.serviceName}
                        {r.professionalName ? ` · ${r.professionalName}` : ""}
                      </p>
                    )}
                    <p>{r.createdAt.slice(0, 10)}</p>
                  </div>

                  <Badge variant={r.isPublic ? "secondary" : "outline"} className="text-[10px]">
                    {r.isPublic ? "Pública" : "Oculta"}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </ClinicalPageShell>
  );
}
